'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const pipelineSteps = [
{
  step: '01',
  label: 'Input',
  title: 'Chick Procurement',
  detail: '150,000 Lohmann Brown chicks × 3 cycles annually',
  metric: '450K',
  metricLabel: 'chicks/year',
  icon:
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 22C6.5 22 2 17.5 2 12A10 10 0 0 1 12 2c.5 0 1 0 1.5.1" />
        <path d="M17 7h.01M15 11h.01M19 11h.01M17 15h.01M22 5a2 2 0 1 1-4 0 2 2 0 0 1 4 0z" />
      </svg>

},
{
  step: '02',
  label: 'Growth',
  title: 'Maturation Pipeline',
  detail: '19-week automated climate-controlled rearing',
  metric: '19wk',
  metricLabel: 'to production',
  icon:
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>

},
{
  step: '03',
  label: 'Production',
  title: 'Egg Output',
  detail: '4M+ eggs annually from automated laying systems',
  metric: '4M+',
  metricLabel: 'eggs/year',
  icon:
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <ellipse cx="12" cy="13" rx="6" ry="8" />
      </svg>

},
{
  step: '04',
  label: 'Revenue',
  title: 'Multi-Stream Output',
  detail: 'Eggs + chicken meat + 10,000 tons manure annually',
  metric: '3×',
  metricLabel: 'revenue streams',
  icon:
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>

}];


const revenueStreams = [
{ label: 'Egg Sales', description: 'Primary revenue: 4M+ eggs/year at market price', color: '#FACC15', pct: 75 },
{ label: 'Chicken Meat', description: 'End-of-cycle birds sold at peak weight', color: '#16A34A', pct: 15 },
{ label: 'Manure (Organic)', description: 'Up to 10,000 tons/year as fertilizer', color: '#22C55E', pct: 10 }];


export default function SolutionSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="solution"
      ref={sectionRef}
      className="py-32 lg:py-40 bg-off-white"
      aria-labelledby="solution-heading">
      
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Label */}
        <div className="scroll-reveal mb-6">
          <span className="section-label">02 / The System</span>
        </div>

        {/* Headline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
          <div className="lg:col-span-7 scroll-reveal">
            <h2 id="solution-heading" className="font-display text-[clamp(2.5rem,5.5vw,4.5rem)] leading-[0.9] tracking-[-0.04em] text-green-dark">
              A precision-controlled<br />
              <span className="text-green-primary">production system</span><br />
              optimized for yield.
            </h2>
          </div>
          <div className="lg:col-span-5 scroll-reveal" style={{ transitionDelay: '100ms' }}>
            <p className="text-lg text-text-muted leading-relaxed mt-4 lg:mt-6">
              Batis Poultry operates as a closed-loop agricultural production engine. Disease-resistant breeds, automated climate control, and sustainable waste-to-value systems deliver predictable, high-volume output at industrial scale.
            </p>
          </div>
        </div>

        {/* Pipeline */}
        <div className="scroll-reveal mb-20" style={{ transitionDelay: '200ms' }}>
          <div className="relative">
            {/* Connector line */}
            <div
              className="hidden lg:block absolute top-[60px] left-[12.5%] right-[12.5%] h-px"
              style={{ background: 'linear-gradient(90deg, transparent, #16A34A 20%, #16A34A 80%, transparent)' }}
              aria-hidden="true" />
            

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
              {pipelineSteps?.map((step, idx) =>
              <div
                key={step?.step}
                className="scroll-reveal glass-green rounded-2xl p-6 hover-lift relative"
                style={{ transitionDelay: `${idx * 120}ms` }}>
                
                  {/* Step badge */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-10 h-10 rounded-full bg-green-primary/15 border border-green-primary/30 flex items-center justify-center text-green-primary">
                      {step?.icon}
                    </div>
                    <span className="font-mono-custom text-xs text-green-primary/40 font-medium">{step?.label}</span>
                  </div>

                  <div className="metric-number text-3xl mb-1" style={{ color: idx === 0 ? '#f8c600' : '#FACC15' }}>
                    {step?.metric}
                  </div>
                  <p className="text-xs text-green-dark/40 font-mono-custom mb-3">{step?.metricLabel}</p>
                  <h3 className="font-display text-lg text-green-dark tracking-tight mb-2">{step?.title}</h3>
                  <p className="text-sm text-text-muted leading-relaxed">{step?.detail}</p>

                  {/* Arrow connector (mobile) */}
                  {idx < pipelineSteps?.length - 1 &&
                <div className="lg:hidden flex justify-center mt-4" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 5v14M5 12l7 7 7-7" />
                      </svg>
                    </div>
                }
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Asymmetric bento: image + revenue streams */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Image */}
          <div className="scroll-reveal-left lg:col-span-5 img-overlay rounded-2xl overflow-hidden" style={{ minHeight: '400px' }}>
            <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_1889a6a8b-1768060555129.png"
              alt="Modern automated poultry facility interior showing climate-controlled egg production rows"
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 42vw" />
            
          </div>

          {/* Revenue streams */}
          <div className="scroll-reveal-right lg:col-span-7 flex flex-col gap-4">
            <div
              className="rounded-2xl p-7"
              style={{ background: 'linear-gradient(135deg, #0A1F0F 0%, #0F2D18 100%)' }}>
              
              <p className="section-label mb-2">Revenue Architecture</p>
              <h3 className="font-display text-2xl text-white tracking-tight mb-6">
                Three revenue streams.<br />One integrated system.
              </h3>
              <div className="space-y-5">
                {revenueStreams?.map((stream, idx) =>
                <div key={stream?.label} style={{ transitionDelay: `${idx * 120}ms` }}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-white/80">{stream?.label}</span>
                      <span className="text-sm font-bold" style={{ color: stream?.color }}>{stream?.pct}%</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div
                      className="h-full rounded-full progress-bar"
                      style={{ width: `${stream?.pct}%`, background: stream?.color, animationDelay: `${idx * 0.2}s` }} />
                    
                    </div>
                    <p className="text-xs text-white/30 mt-1.5">{stream?.description}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Key specs */}
            <div className="grid grid-cols-3 gap-3">
              {[
              { label: 'Breed', value: 'Lohmann Brown', sub: 'Disease-resistant' },
              { label: 'Manure Output', value: '10,000T', sub: 'per year' },
              { label: 'Cycles/Year', value: '3×', sub: '150K chicks each' }]?.
              map((spec) =>
              <div key={spec?.label} className="glass-green rounded-xl p-4 text-center">
                  <div className="font-display text-lg text-green-dark tracking-tight">{spec?.value}</div>
                  <p className="text-xs text-text-muted mt-1">{spec?.label}</p>
                  <p className="text-xs text-green-primary/60">{spec?.sub}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

}